/**
 * `useNocLive` — datos en vivo del NOC (TG-96).
 *
 * Estrategia:
 * - Base REST: `/overview`, `/stations`, `/sessions/active` y `/alerts`.
 * - SSE (`/api/v1/noc/stream`, ADR 0027 vía `useNocStream`): el `snapshot` y los
 *   eventos `alert.*` se aplican al instante; los cambios de estación/conector/
 *   medidor disparan un refresco REST con debounce (la agregación de estado vive
 *   en el backend).
 * - Fallback: si el stream no está disponible, se hace polling cada 3 s.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  LiveSnapshot,
  NocActiveSession,
  NocAlert,
  NocOverview,
  NocSnapshot,
  NocStationSummary,
} from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { nocApi } from '../api/nocApi';
import { useNocStream, type NocStreamRecord } from './useNocStream';

export type NocLiveMode = 'connecting' | 'polling' | 'sse';

export interface PowerPoint {
  /** Timestamp (ms) de la muestra. */
  t: number;
  /** Potencia total instantánea (kW). */
  kw: number;
}

export interface NocLiveState {
  overview: NocOverview | null;
  stations: NocStationSummary[];
  sessions: NocActiveSession[];
  alerts: NocAlert[];
  live: LiveSnapshot | null;
  powerHistory: PowerPoint[];
  /** Buffer de eventos SSE recientes (para el feed del Laboratorio). */
  streamEvents: NocStreamRecord[];
  clearStream: () => void;
  mode: NocLiveMode;
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
  refresh: () => void;
}

const POLL_INTERVAL_MS = 3000;
const POLL_INTERVAL_SSE_MS = 15000;
const POWER_HISTORY_LIMIT = 60;
const REFRESH_DEBOUNCE_MS = 800;

export function useNocLive(): NocLiveState {
  const [overview, setOverview] = useState<NocOverview | null>(null);
  const [stations, setStations] = useState<NocStationSummary[]>([]);
  const [sessions, setSessions] = useState<NocActiveSession[]>([]);
  const [alerts, setAlerts] = useState<NocAlert[]>([]);
  const [live, setLive] = useState<LiveSnapshot | null>(null);
  const [powerHistory, setPowerHistory] = useState<PowerPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const inFlightRef = useRef(false);
  const debounceRef = useRef<number | null>(null);
  const stream = useNocStream();

  const upsertAlert = useCallback((alert: NocAlert) => {
    setAlerts((previous) => {
      const index = previous.findIndex((item) => item.id === alert.id);
      if (index === -1) {
        return [alert, ...previous];
      }
      const next = [...previous];
      next[index] = alert;
      return next;
    });
  }, []);

  const fetchSnapshot = useCallback(async (): Promise<void> => {
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    try {
      const [overviewResponse, stationsResponse, sessionsResponse, alertsResponse] = await Promise.all([
        nocApi.overview(),
        nocApi.stations(),
        nocApi.activeSessions(),
        nocApi.alerts().catch(() => null),
      ]);
      setOverview(overviewResponse);
      setStations(stationsResponse.data);
      setSessions(sessionsResponse.data);
      if (alertsResponse) {
        setAlerts(alertsResponse.data);
      }
      setLastUpdated(overviewResponse.generatedAt);
      setError(null);
      setPowerHistory((previous) =>
        [...previous, { t: Date.now(), kw: overviewResponse.power.totalInstantKw }].slice(
          -POWER_HISTORY_LIMIT,
        ),
      );
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(
          caught.status === 403
            ? 'Tu rol no tiene acceso al NOC (requiere SuperAdmin o Soporte N3).'
            : caught.message,
        );
      } else {
        setError('No se pudo actualizar el NOC.');
      }
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, []);

  const scheduleRefresh = useCallback(() => {
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
      debounceRef.current = null;
      void fetchSnapshot();
    }, REFRESH_DEBOUNCE_MS);
  }, [fetchSnapshot]);

  // Polling base (3 s; 15 s de reconciliación cuando el SSE está abierto).
  useEffect(() => {
    void fetchSnapshot();
    const interval = stream.mode === 'open' ? POLL_INTERVAL_SSE_MS : POLL_INTERVAL_MS;
    const intervalId = window.setInterval(() => void fetchSnapshot(), interval);
    return () => window.clearInterval(intervalId);
  }, [fetchSnapshot, refreshKey, stream.mode]);

  // Aplicación de eventos SSE: procesa TODO el buffer nuevo del tick (no solo el
  // último), para no perder eventos simultáneos (p. ej. connector.updated + alert.created).
  const lastSeqRef = useRef(0);
  useEffect(() => {
    const fresh = stream.events.filter((record) => record.seq > lastSeqRef.current);
    if (fresh.length === 0) {
      return;
    }
    const last = stream.events[stream.events.length - 1];
    lastSeqRef.current = last ? last.seq : lastSeqRef.current;

    for (const record of fresh) {
      switch (record.type) {
        case 'snapshot': {
          const snapshot = record.data as NocSnapshot;
          if (Array.isArray(snapshot?.stations)) {
            setStations(snapshot.stations);
          }
          if (snapshot?.live) {
            setLive(snapshot.live);
          }
          if (snapshot?.generatedAt) {
            setLastUpdated(snapshot.generatedAt);
          }
          scheduleRefresh();
          break;
        }
        case 'alert.created':
        case 'alert.updated': {
          const alert = record.data as NocAlert;
          if (alert?.id) {
            upsertAlert(alert);
          }
          break;
        }
        case 'station.updated':
        case 'connector.updated':
        case 'meter.tick':
        case 'session.started':
        case 'session.ended':
          scheduleRefresh();
          break;
        default:
          // `command.*`, `sim.*`: se consumen en el Laboratorio; no cambian el NOC.
          break;
      }
    }
  }, [stream.events, scheduleRefresh, upsertAlert]);

  // Limpieza del debounce al desmontar.
  useEffect(
    () => () => {
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
      }
    },
    [],
  );

  const refresh = useCallback(() => setRefreshKey((key) => key + 1), []);
  const mode: NocLiveMode = stream.mode === 'open' ? 'sse' : loading ? 'connecting' : 'polling';

  return {
    overview,
    stations,
    sessions,
    alerts,
    live,
    powerHistory,
    streamEvents: stream.events,
    clearStream: stream.clear,
    mode,
    loading,
    error,
    lastUpdated,
    refresh,
  };
}
